/**
 * JARVIS UI theme tokens — app chrome (Joy), not PDF/document branding.
 * PDF header/footer/logo colours live in pdf_export_config (Admin → PDF Export).
 */

export type JarvisUiThemeTokens = {
  /** Main interactive / brand colour (solid buttons, links, selected chrome). */
  primary: string;
  /** Accent / highlight colour (hovers, soft selection wash, scrollbar). */
  accent: string;
  /** Hover fill for selectable blocks (stage tabs, lifecycle tanks). */
  hoverBg: string;
  /** Hover border for selectable blocks. */
  hoverBorder: string;
  /** Text colour on hover fill (and solid-button hover when accent is used). */
  hoverText: string;
  /** Main nav / sidebar background. */
  navBg: string;
  /** Main nav item + icon text. */
  navText: string;
  /** Main nav muted text (brand subtitle, dividers context). */
  navMutedText: string;
  /** Main nav right border / section dividers. */
  navBorder: string;
  /** Selected nav item block fill. */
  navSelectedBg: string;
  /** Selected nav item text/icon. */
  navSelectedText: string;
  /** Hover nav item block fill. */
  navHoverBg: string;
  /** Hover nav item text/icon. */
  navHoverText: string;
  /** Nav menu scrollbar track. */
  navScrollbarTrack: string;
  /** Nav menu scrollbar thumb. */
  navScrollbarThumb: string;
  /** Nav menu scrollbar thumb on hover. */
  navScrollbarThumbHover: string;
  /** Icon chip background inside workspace / content cards. */
  cardIconBg: string;
  /** Icon colour inside those chips (set white on dark chips). */
  cardIconColor: string;
  /** Product-type / filter chip idle background. */
  chipBg: string;
  /** Product-type / filter chip idle text + icon. */
  chipText: string;
  /** Product-type / filter chip selected background. */
  chipSelectedBg: string;
  /** Product-type / filter chip selected text + icon. */
  chipSelectedText: string;
  /** Outline chip idle background (Products card). */
  chipOutlineBg: string;
  /** Outline chip idle text + icon. */
  chipOutlineText: string;
  /** Outline chip selected background. */
  chipOutlineSelectedBg: string;
  /** Outline chip selected text + icon. */
  chipOutlineSelectedText: string;
  /** Outline chip selected border. */
  chipOutlineSelectedBorder: string;
  /** Selected list/tab item background (document rows, survey tabs, supplier survey picker). */
  selectionBg: string;
  /** Selected list/tab item text. */
  selectionText: string;
  /** Workspace stage tab idle background (Overview, Quotes, …). */
  stageBg: string;
  /** Workspace stage tab idle text. */
  stageText: string;
  /** Workspace stage tab idle border. */
  stageBorder: string;
  /** Workspace stage tab selected background. */
  stageSelectedBg: string;
  /** Workspace stage tab selected text. */
  stageSelectedText: string;
  /** Workspace stage tab selected border. */
  stageSelectedBorder: string;
  /** Number/badge circle idle background on stage tabs. */
  stageBadgeBg: string;
  /** Number/badge circle idle text. */
  stageBadgeText: string;
  /** Number/badge circle selected background. */
  stageSelectedBadgeBg: string;
  /** Number/badge circle selected text. */
  stageSelectedBadgeText: string;
  /** Lifecycle card progress sub-number (idle). */
  stageMetaText: string;
  /** Lifecycle card progress sub-number (selected). */
  stageSelectedMetaText: string;
  /** Lifecycle card progress sub-number (hover). */
  stageMetaHoverText: string;
  /** Page / body background. */
  backgroundBody: string;
  /** Card / surface background. */
  backgroundSurface: string;
  /** Subtle elevated / sidebar-adjacent background. */
  backgroundLevel1: string;
  /** Primary text. */
  textPrimary: string;
  /** Secondary / muted text. */
  textSecondary: string;
  /** App shell font stack. */
  fontFamily: string;
};

export type JarvisUiThemeDefinition = {
  /** Stable key: `default` is built-in; custom keys are slugified names. */
  key: string;
  name: string;
  /** Built-in themes cannot be deleted or overwritten from Admin. */
  builtin: boolean;
  tokens: JarvisUiThemeTokens;
  /** Present when loaded from the database. */
  id?: string;
  updatedAt?: string;
};

export function mixHex(hex: string, towardWhite: number): string {
  const raw = hex.replace('#', '');
  const full = raw.length === 3
    ? raw.split('').map((c) => c + c).join('')
    : raw.padEnd(6, '0').slice(0, 6);
  const num = Number.parseInt(full, 16);
  if (!Number.isFinite(num)) return '#FFF0CC';
  const t = Math.max(0, Math.min(1, towardWhite));
  const mix = (channel: number) => Math.round(channel + (255 - channel) * t);
  const r = mix((num >> 16) & 255);
  const g = mix((num >> 8) & 255);
  const b = mix(num & 255);
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

/** Snapshot of the live system before custom theming existed. */
export const DEFAULT_UI_THEME_TOKENS: JarvisUiThemeTokens = {
  // Joy UI stock primary (what CssVarsProvider used with no extendTheme).
  primary: '#0B6BCB',
  // Historic accent from index.css scrollbar hover.
  accent: '#FCC14D',
  hoverBg: mixHex('#FCC14D', 0.72),
  hoverBorder: '#FCC14D',
  hoverText: '#0A0A0A',
  // Sidebar was hardcoded white + dark text + primary.softBg selection.
  navBg: '#FFFFFF',
  navText: '#32383E',
  navMutedText: '#555E68',
  navBorder: '#DDE7EE',
  navSelectedBg: mixHex('#0B6BCB', 0.88),
  navSelectedText: '#0A2744',
  navHoverBg: mixHex('#FCC14D', 0.72),
  navHoverText: '#0A0A0A',
  navScrollbarTrack: '#F5F5F5',
  navScrollbarThumb: '#E0E0E0',
  navScrollbarThumbHover: '#FCC14D',
  // Quote/invoice lane chips were primary.50 / primary.600.
  cardIconBg: mixHex('#0B6BCB', 0.88),
  cardIconColor: '#12467B',
  // Product type chips (Glass / Windows / …) — outlined idle, soft selected.
  chipBg: '#FFFFFF',
  chipText: '#32383E',
  chipSelectedBg: mixHex('#0B6BCB', 0.88),
  chipSelectedText: '#0A2744',
  // Outline chips (Products editor) — border callout, light fill.
  chipOutlineBg: '#FFFFFF',
  chipOutlineText: '#32383E',
  chipOutlineSelectedBg: '#FFFFFF',
  chipOutlineSelectedText: '#0A2744',
  chipOutlineSelectedBorder: '#FCC14D',
  // Selected list / tab highlight (document rows, survey tabs, supplier picker).
  selectionBg: '#FCC14D',
  selectionText: '#212D3B',
  // Workspace stage strip (Overview / Quotes / Invoices …) idle + selected.
  stageBg: '#F0F4F8',
  stageText: '#32383E',
  stageBorder: '#DDE7EE',
  stageSelectedBg: mixHex('#0B6BCB', 0.88),
  stageSelectedText: '#0A2744',
  stageSelectedBorder: mixHex('#0B6BCB', 0.45),
  stageBadgeBg: '#CDD7E1',
  stageBadgeText: '#555E68',
  stageSelectedBadgeBg: mixHex('#0B6BCB', 0.78),
  stageSelectedBadgeText: '#0A2744',
  stageMetaText: '#555E68',
  stageSelectedMetaText: '#9FA6AD',
  stageMetaHoverText: '#0A0A0A',
  backgroundBody: '#FFFFFF',
  backgroundSurface: '#FFFFFF',
  backgroundLevel1: '#F0F4F8',
  textPrimary: '#0A0A0A',
  textSecondary: '#555E68',
  fontFamily: "'Poppins', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
};

export const DEFAULT_UI_THEME: JarvisUiThemeDefinition = {
  key: 'default',
  name: 'Default',
  builtin: true,
  tokens: DEFAULT_UI_THEME_TOKENS,
};

export function slugifyThemeKey(name: string): string {
  const base = String(name || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
  return base || `theme-${Date.now().toString(36)}`;
}

export function isHexColour(value: string): boolean {
  return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(String(value || '').trim());
}

export function softAccentFill(accent: string): string {
  return mixHex(accent, 0.72);
}

export function normalizeTokens(input: Partial<JarvisUiThemeTokens> | null | undefined): JarvisUiThemeTokens {
  const src = input || {};
  const pick = (key: keyof JarvisUiThemeTokens, fallback: string) => {
    const raw = String(src[key] || '').trim();
    if (key === 'fontFamily') return raw || fallback;
    return isHexColour(raw) ? raw : fallback;
  };
  const accent = pick('accent', DEFAULT_UI_THEME_TOKENS.accent);
  const primary = pick('primary', DEFAULT_UI_THEME_TOKENS.primary);
  return {
    primary,
    accent,
    hoverBg: pick('hoverBg', softAccentFill(accent)),
    hoverBorder: pick('hoverBorder', accent),
    hoverText: pick('hoverText', DEFAULT_UI_THEME_TOKENS.hoverText),
    navBg: pick('navBg', DEFAULT_UI_THEME_TOKENS.navBg),
    navText: pick('navText', DEFAULT_UI_THEME_TOKENS.navText),
    navMutedText: pick('navMutedText', DEFAULT_UI_THEME_TOKENS.navMutedText),
    navBorder: pick('navBorder', DEFAULT_UI_THEME_TOKENS.navBorder),
    navSelectedBg: pick('navSelectedBg', softAccentFill(primary)),
    navSelectedText: pick('navSelectedText', DEFAULT_UI_THEME_TOKENS.navSelectedText),
    navHoverBg: pick('navHoverBg', softAccentFill(accent)),
    navHoverText: pick('navHoverText', DEFAULT_UI_THEME_TOKENS.navHoverText),
    navScrollbarTrack: pick('navScrollbarTrack', DEFAULT_UI_THEME_TOKENS.navScrollbarTrack),
    navScrollbarThumb: pick('navScrollbarThumb', DEFAULT_UI_THEME_TOKENS.navScrollbarThumb),
    navScrollbarThumbHover: pick('navScrollbarThumbHover', accent),
    cardIconBg: pick('cardIconBg', softAccentFill(primary)),
    cardIconColor: pick('cardIconColor', DEFAULT_UI_THEME_TOKENS.cardIconColor),
    chipBg: pick('chipBg', DEFAULT_UI_THEME_TOKENS.chipBg),
    chipText: pick('chipText', DEFAULT_UI_THEME_TOKENS.chipText),
    chipSelectedBg: pick('chipSelectedBg', softAccentFill(primary)),
    chipSelectedText: pick('chipSelectedText', DEFAULT_UI_THEME_TOKENS.chipSelectedText),
    chipOutlineBg: pick('chipOutlineBg', DEFAULT_UI_THEME_TOKENS.chipOutlineBg),
    chipOutlineText: pick('chipOutlineText', DEFAULT_UI_THEME_TOKENS.chipOutlineText),
    chipOutlineSelectedBg: pick('chipOutlineSelectedBg', DEFAULT_UI_THEME_TOKENS.chipOutlineSelectedBg),
    chipOutlineSelectedText: pick('chipOutlineSelectedText', DEFAULT_UI_THEME_TOKENS.chipOutlineSelectedText),
    chipOutlineSelectedBorder: pick('chipOutlineSelectedBorder', accent),
    selectionBg: pick(
      'selectionBg',
      isHexColour(String((src as { docRowSelectedBg?: string }).docRowSelectedBg || ''))
        ? String((src as { docRowSelectedBg?: string }).docRowSelectedBg)
        : DEFAULT_UI_THEME_TOKENS.selectionBg,
    ),
    selectionText: pick(
      'selectionText',
      isHexColour(String((src as { docRowSelectedText?: string }).docRowSelectedText || ''))
        ? String((src as { docRowSelectedText?: string }).docRowSelectedText)
        : DEFAULT_UI_THEME_TOKENS.selectionText,
    ),
    stageBg: pick('stageBg', DEFAULT_UI_THEME_TOKENS.stageBg),
    stageText: pick('stageText', DEFAULT_UI_THEME_TOKENS.stageText),
    stageBorder: pick('stageBorder', DEFAULT_UI_THEME_TOKENS.stageBorder),
    stageSelectedBg: pick('stageSelectedBg', softAccentFill(primary)),
    stageSelectedText: pick('stageSelectedText', DEFAULT_UI_THEME_TOKENS.stageSelectedText),
    stageSelectedBorder: pick('stageSelectedBorder', mixHex(primary, 0.45)),
    stageBadgeBg: pick('stageBadgeBg', DEFAULT_UI_THEME_TOKENS.stageBadgeBg),
    stageBadgeText: pick('stageBadgeText', DEFAULT_UI_THEME_TOKENS.stageBadgeText),
    stageSelectedBadgeBg: pick('stageSelectedBadgeBg', mixHex(primary, 0.78)),
    stageSelectedBadgeText: pick('stageSelectedBadgeText', DEFAULT_UI_THEME_TOKENS.stageSelectedBadgeText),
    stageMetaText: pick('stageMetaText', DEFAULT_UI_THEME_TOKENS.stageMetaText),
    stageSelectedMetaText: pick('stageSelectedMetaText', DEFAULT_UI_THEME_TOKENS.stageSelectedMetaText),
    stageMetaHoverText: pick('stageMetaHoverText', DEFAULT_UI_THEME_TOKENS.hoverText),
    backgroundBody: pick('backgroundBody', DEFAULT_UI_THEME_TOKENS.backgroundBody),
    backgroundSurface: pick('backgroundSurface', DEFAULT_UI_THEME_TOKENS.backgroundSurface),
    backgroundLevel1: pick('backgroundLevel1', DEFAULT_UI_THEME_TOKENS.backgroundLevel1),
    textPrimary: pick('textPrimary', DEFAULT_UI_THEME_TOKENS.textPrimary),
    textSecondary: pick('textSecondary', DEFAULT_UI_THEME_TOKENS.textSecondary),
    fontFamily: pick('fontFamily', DEFAULT_UI_THEME_TOKENS.fontFamily),
  };
}
